import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import type { StrategyListItemV2 } from "../../types/strategy";

export interface UpdateEntryThresholdDialogProps {
  open: boolean;
  strategy: StrategyListItemV2 | null;
  onClose: () => void;
  onConfirm: (entryDifferenceRatio: number) => Promise<boolean>;
}

export const UpdateEntryThresholdDialog: React.FC<
  UpdateEntryThresholdDialogProps
> = ({ open, strategy, onClose, onConfirm }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const initializedStrategyId = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      initializedStrategyId.current = null;
      return;
    }
    if (!strategy || initializedStrategyId.current === strategy.strategy_id) {
      return;
    }
    initializedStrategyId.current = strategy.strategy_id;
    setValue(String(strategy.entry_diff_ratio * 100));
    setError("");
    setLoading(false);
  }, [open, strategy]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleConfirm = async () => {
    if (loading) return;
    const percentage = Number(value);
    if (value.trim() === "" || !Number.isFinite(percentage)) {
      setError(t("traderDashboard.updateEntryThreshold.errorInvalidValue"));
      return;
    }

    setLoading(true);
    setError("");
    try {
      const success = await onConfirm(percentage / 100);
      if (success) {
        onClose();
      } else {
        setError(t("traderDashboard.updateEntryThreshold.errorRequestFailed"));
      }
    } catch {
      setError(t("traderDashboard.updateEntryThreshold.errorRequestFailed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t("traderDashboard.updateEntryThreshold.title", {
          symbol: strategy?.symbol ?? "-",
        })}
      </DialogTitle>
      <DialogContent>
        <p>{t("traderDashboard.updateEntryThreshold.description")}</p>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          fullWidth
          type="number"
          label={t("traderDashboard.updateEntryThreshold.fieldLabel")}
          helperText={t("traderDashboard.updateEntryThreshold.helperText")}
          value={value}
          disabled={loading}
          onChange={(event) => setValue(event.target.value)}
          inputProps={{ step: "any" }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {t("traderDashboard.updateEntryThreshold.cancel")}
        </Button>
        <Button onClick={handleConfirm} disabled={loading} variant="contained">
          {loading
            ? t("traderDashboard.updateEntryThreshold.updating")
            : t("traderDashboard.updateEntryThreshold.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateEntryThresholdDialog;
